import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { boardCells, boardSize, totalPicks } from "../features/hex/board";
import { HexBoard } from "../features/hex/HexBoard";
import { ApiError } from "../lib/api";
import { submit } from "../lib/runtime";
import { useCanvasState } from "../lib/useCanvasState";

const ALREADY_PICKED = "sender_already_counted";

/** The viewer canvas: one pick each, taken back when the board resets. */
export const Route = createFileRoute("/hex/viewer/$choiceMount/$boardMount")({
  component: ViewerCanvas,
});

function ViewerCanvas() {
  const { choiceMount, boardMount } = Route.useParams();
  const choices = useCanvasState(choiceMount);
  const game = useCanvasState(boardMount);
  const [picked, setPicked] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const cells = boardCells(choices.state, game.state);
  const { columns, rows } = boardSize(choices.state?.entities ?? []);
  const total = totalPicks(cells);

  /** A new run is a clean slate, whatever the tallies say. The mount id is the run. */
  useEffect(() => {
    setPicked(null);
    setRefused(null);
  }, [choiceMount]);

  /** A round reopening on the same run, which is what a reset looks like from here. */
  useEffect(() => {
    if (total === 0) {
      setPicked(null);
      setRefused(null);
    }
  }, [total]);

  if (choices.error !== null) return <Problem what={choices.error} />;
  if (columns === 0) return <Waiting />;

  return (
    <main style={{ position: "fixed", inset: 0, background: "#0b0d10" }}>
      <HexBoard
        columns={columns}
        rows={rows}
        cells={cells}
        look="control"
        picked={picked}
        onPick={(id) => {
          if (picked !== null) return;
          setPicked(id);
          submit(choiceMount, "pick", id).catch((error: unknown) => {
            setPicked(null);
            const already = error instanceof ApiError && error.code === ALREADY_PICKED;
            setRefused(already ? "you have already picked this round" : "that pick did not count");
          });
        }}
      />
      <Banner text={refused ?? (picked === null ? "pick a hex" : `you picked ${picked}`)} />
    </main>
  );
}

function Banner({ text }: { text: string }) {
  return (
    <p
      style={{
        position: "absolute",
        left: 12,
        bottom: 10,
        margin: 0,
        color: "rgba(255,255,255,0.65)",
        font: "13px ui-monospace, monospace",
        pointerEvents: "none",
      }}
    >
      {text}
    </p>
  );
}

function Waiting() {
  return (
    <main style={{ position: "fixed", inset: 0, background: "#0b0d10" }}>
      <Banner text="waiting for the board" />
    </main>
  );
}

function Problem({ what }: { what: string }) {
  return (
    <main style={{ padding: 24, fontFamily: "monospace", color: "#eee", background: "#0b0d10" }}>
      <h1>Cannot read this run</h1>
      <p>{what}</p>
      <p>Sign in first at /v1/auth/twitch/start?as=viewer, then reload.</p>
    </main>
  );
}
